/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import classNames from 'classnames'
import Image, { type StaticImageData } from 'next/image'
import { InlineVideo } from 'components/inline-video'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'
import Actions from 'components/actions'
import type { ActionsProps } from 'components/actions/types'

import applicationsImage from './img/applications.svg'
import applicationsImageSmall from './img/applications-small.svg'
import infrastructureImage from './img/infrastructure.svg'
import infrastructureImageSmall from './img/infrastructure-small.svg'
import networkingImage from './img/networking.svg'
import networkingImageSmall from './img/networking-small.svg'
import securityImage from './img/security.svg'
import securityImageSmall from './img/security-small.svg'

import Eyebrow from '../eyebrow'
import s from './style.module.css'
import { vercelSplit } from '@web/cms'

const FALLBACK_IMAGE_MAP = new Map<string, StaticImageData>([
	['applications', applicationsImage],
	['infrastructure', infrastructureImage],
	['networking', networkingImage],
	['security', securityImage],
])

const SMALL_IMAGE_MAP = new Map<string, StaticImageData>([
	['applications-small', applicationsImageSmall],
	['infrastructure-small', infrastructureImageSmall],
	['networking-small', networkingImageSmall],
	['security-small', securityImageSmall],
])

export interface HeroProps {
	title: string
	description: string
	// TODO: determine if ctas are required
	ctas?: Array<{
		title: string
		url: string
	}>
	image: {
		src: string
	}
	solutionSpace: SolutionSpaces
	videoUrl?: string
	hideBackground?: boolean
}

export default function SolutionsHero({
	title,
	description,
	ctas,
	image,
	solutionSpace,
	videoUrl,
	hideBackground = false,
}: HeroProps) {
	const splitSolutionSpace = vercelSplit(solutionSpace)

	const heroImage =
		image || FALLBACK_IMAGE_MAP.get(splitSolutionSpace || 'infrastructure')

	return (
		<header className={s.root}>
			<div className={s.bgBounding}>
				<div
					className={classNames(s.container, {
						[s.hasPadding]: hideBackground,
					})}
				>
					{videoUrl ? (
						<div className={s.video}>
							<InlineVideo
								url={videoUrl}
								gradientPosition={false}
								appearance={'dark'}
							/>
						</div>
					) : null}
					<div className={s.content}>
						{/* TODO Investigate pulling in eyebrow text from a hero prop instead of hardcoding within each hero */}
						{/* Asana task: https://app.asana.com/0/346384260719996/1202584788187847 */}
						<Eyebrow
							appearance="dark"
							theme={splitSolutionSpace || 'infrastructure'}
							text="Solution"
						/>
						<h1 className={s.title}>{title}</h1>
						<p className={s.description}>{description}</p>
						{ctas.length > 0 ? (
							<Actions
								appearance="dark"
								ctas={
									ctas.map(({ url, title }, idx) => {
										return {
											title: title,
											href: parseUrl(url).href,
											type: idx === 0 ? 'button' : 'standalone-link',
										}
									}) as ActionsProps['ctas']
								}
							/>
						) : null}
					</div>
				</div>

				<div className={s.desktopMedia}>
					{!hideBackground ? (
						<Image src={heroImage.src} alt="" fill={true} priority={true} />
					) : null}
				</div>
				<div className={s.smallMedia}>
					{!hideBackground ? (
						<Image
							src={
								SMALL_IMAGE_MAP.get(
									`${splitSolutionSpace || 'infrastructure'}-small`,
								).src
							}
							alt=""
							width={800}
							height={667}
							priority={true}
						/>
					) : null}
				</div>
			</div>
		</header>
	)
}
